import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles, TextField } from '@material-ui/core';
import { FormatBold, FormatItalic, FormatUnderlined, Code, Delete, Link, LinkOff } from '@material-ui/icons';
import { RichUtils, EditorState, CompositeDecorator, getDefaultKeyBinding } from 'draft-js';
import PluginEditor from '@draft-js-plugins/editor';
import React, { useContext, useState } from 'react';
import EmployeeInfoContext from '../../../contexts/EmployeeInfoContext';
import clsx from 'clsx';
import 'draft-js/dist/Draft.css';
import './DraftEditorStyles.css';
import '@draft-js-plugins/linkify/lib/plugin.css';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import createListPlugin from 'draft-js-list-plugin';

const listPlugin = createListPlugin();
const linkifyPlugin = createLinkifyPlugin();

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  editor: {
    minHeight: '40vh',
  },
  editorHeader: {
    display: 'grid',
    width: '100%',
    minHeight: theme.spacing(3),
    lineHeight: theme.spacing(3),
    fontSize: theme.spacing(3),
    backgroundColor: 'rgba(0,0,0,.5)',
    gridTemplateColumns: 'repeat(12,1fr)',
  },
  broadcastContainer: {
    maxWidth: '500px',
    margin: 'auto',
  },
  editorHeaderIcon: {
    height: theme.spacing(2) + 'px !important',
    width: theme.spacing(2) + 'px !important',
    padding: theme.spacing(0.5),
    margin: 'auto',
  },
  editorHeaderIconButton: {
    borderRadius: 0,
  },
  link: {},
}));

export default function DraftEditor({ draftRef, editorState, onEditorChange, onDelete, saving, readOnly }: any) {
  const classes = useStyles();

  const Editor = PluginEditor as any;

  const [employeeInfo] = useContext(EmployeeInfoContext);

  const [showUrlInput, setShowUrlInput] = useState<boolean>(false);
  const [urlValue, setUrlValue] = useState<string>('');

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      onEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const onBold = () => toggleInlineStyle('BOLD');
  const onUnderline = () => toggleInlineStyle('UNDERLINE');
  const onItalics = () => toggleInlineStyle('BOLD');
  const onCode = () => onEditorChange(RichUtils.toggleCode(editorState));
  const onKeyBinding = (e) => {
    if (e.keyCode === 9) e.preventDefault();

    return getDefaultKeyBinding(e);
  };

  const toggleInlineStyle = (type: string) => onEditorChange(RichUtils.toggleInlineStyle(editorState, type));

  const promptForLink = (e) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = editorState.getSelection().getStartKey();
      const startOffset = editorState.getSelection().getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

      let url = '';
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        url = linkInstance.getData().url;
      }

      setShowUrlInput(true);
      setUrlValue(url);
    }
  };

  const confirmLink = (e) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url: urlValue });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    onEditorChange(RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey));
    setShowUrlInput(false);
    setUrlValue('');
  };

  const removeLink = (e) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) onEditorChange(RichUtils.toggleLink(editorState, selection, null));
  };

  const onLinkKeyPress = (e) => {
    if (e.which === 13) confirmLink(e);
  };

  const onChange = (editor) => {
    onEditorChange(editor);
  };

  return (
    <>
      {employeeInfo?.isInRole('Admin') ? (
        <div className={classes.editorHeader}>
          <IconButton onClick={onBold} className={classes.editorHeaderIconButton} size='small'>
            <FormatBold className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={onItalics} className={classes.editorHeaderIconButton} size='small'>
            <FormatItalic className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={onUnderline} className={classes.editorHeaderIconButton} size='small'>
            <FormatUnderlined className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={onCode} className={classes.editorHeaderIconButton} size='small'>
            <Code className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={promptForLink} className={classes.editorHeaderIconButton} size='small'>
            <Link className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={removeLink} className={classes.editorHeaderIconButton} size='small'>
            <LinkOff className={classes.editorHeaderIcon} />
          </IconButton>
          <IconButton onClick={onDelete} className={classes.editorHeaderIconButton} size='small'>
            <Delete className={classes.editorHeaderIcon} />
          </IconButton>
          {saving ? <CircularProgress style={{ gridColumn: '12 / span 1' }} className={clsx(classes.editorHeaderIcon)} /> : null}
        </div>
      ) : null}
      <Editor
        ref={draftRef}
        editorState={editorState}
        onChange={onChange}
        handleKeyCommand={handleKeyCommand}
        readOnly={readOnly || !employeeInfo?.isInRole('Admin')}
        keyBindingFn={onKeyBinding}
        decorators={[decorator]}
        plugins={[linkifyPlugin, listPlugin]}
      />
      <Dialog open={showUrlInput}>
        <DialogTitle>Edit Link</DialogTitle>
        <DialogContent>
          <TextField value={urlValue} onChange={(e) => setUrlValue(e.target.value)} onKeyPress={onLinkKeyPress} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUrlInput(false)} color='primary'>
            Close
          </Button>
          <Button onClick={confirmLink} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
  }, callback);
}

export const DraftLink = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  const classes = useStyles();
  return (
    <a href={url} target={'_blank'} rel='noopener noreferrer' className={classes.link}>
      {props.children}
    </a>
  );
};

export const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: DraftLink,
  },
]);
